/**
 * AddToAgentModal
 * ===============
 * Modal for attaching a capability (app or integration) to an agent.
 * Shows agent selector dropdown + optional "Create New Agent" option.
 * Handles missing connection by triggering QuickConnect inline.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  CheckCircle,
  Robot,
  CircleNotch,
  CaretDown,
  Plus,
  Lightning,
  Plug,
  WarningCircle,
  Link as LinkIcon,
  ArrowRight,
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const AddToAgentModal = ({ capability, onClose, onSuccess, onConnectNeeded }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Fetch agents on mount
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/unified-capabilities/agents-list`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setAgents(data.agents || []);
          // Pre-select first agent
          if (data.agents?.length > 0) {
            setSelectedAgentId(data.agents[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch agents:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);

  const selectedAgent = agents.find(a => a.id === selectedAgentId);

  const handleAttach = useCallback(async () => {
    if (!selectedAgentId) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/unified-capabilities/attach-to-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agent_id: selectedAgentId,
          capability_type: capability.capability_type || (capability.app_id ? 'app' : 'integration'),
          capability_id: capability.capability_id || capability.app_id || capability.provider_id,
          capability_name: capability.name || capability.label || '',
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setResult(data);

        // If integration requires connection, offer connect flow
        if (data.requires_connection) {
          // Still show success but with connect prompt
        }

        onSuccess?.(data);
      } else {
        setError(data.detail || data.message || 'Failed to attach capability');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  }, [selectedAgentId, capability, onSuccess]);

  const handleConnectNow = useCallback(() => {
    onClose();
    onConnectNeeded?.(capability);
  }, [capability, onClose, onConnectNeeded]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
        data-testid="add-to-agent-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="w-full max-w-md bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl shadow-2xl overflow-hidden"
          data-testid="add-to-agent-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-default)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--brand-primary)]/15">
                <LinkIcon size={18} weight="bold" className="text-[var(--brand-primary)]" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white" data-testid="add-to-agent-title">
                  Add to Agent
                </h3>
                <p className="text-xs text-[var(--text-muted)] mt-0.5">
                  Make this capability available in an agent
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={submitting}
              className="p-1.5 rounded-md hover:bg-[var(--surface-3)] text-[var(--text-muted)] hover:text-white transition-colors"
              data-testid="add-to-agent-close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            {/* Success State */}
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center gap-3 py-4"
                data-testid="add-to-agent-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                >
                  <CheckCircle size={48} weight="fill" className="text-emerald-400" />
                </motion.div>
                <p className="text-sm font-medium text-white text-center">{result.message}</p>

                {result.already_attached && (
                  <p className="text-xs text-[var(--text-muted)] text-center">
                    This capability was already attached to this agent.
                  </p>
                )}

                {result.requires_connection && (
                  <div className="w-full mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-start gap-2.5">
                      <WarningCircle size={16} weight="fill" className="text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-amber-300">
                          This integration needs to be connected before it can be used.
                        </p>
                        <button
                          onClick={handleConnectNow}
                          className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-400 hover:text-amber-300 transition-colors"
                          data-testid="connect-now-button"
                        >
                          <Plug size={12} weight="bold" />
                          Connect now
                          <ArrowRight size={10} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {!result.requires_connection && !result.already_attached && (
                  <p className="text-xs text-[var(--text-muted)] text-center">
                    Available immediately in Visual Flow & agent execution.
                  </p>
                )}
              </motion.div>
            )}

            {/* Selection Form */}
            {!result && (
              <>
                {/* Capability being added */}
                <div className="mb-4 p-3 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)]" data-testid="capability-preview">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[var(--brand-primary)]/10">
                      <Lightning size={16} weight="bold" className="text-[var(--brand-primary)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {capability?.name || capability?.label || 'Capability'}
                      </p>
                      <p className="text-[10px] text-[var(--text-muted)] truncate">
                        {capability?.description || capability?.short_description || ''}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mb-3 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20"
                    data-testid="add-to-agent-error"
                  >
                    <WarningCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-300">{error}</p>
                  </motion.div>
                )}

                {/* Agent Selector */}
                <div className="mb-4">
                  <label className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 block">
                    Select Agent
                  </label>

                  {loading ? (
                    <div className="flex items-center gap-2 p-3 bg-[var(--surface-2)] rounded-lg">
                      <CircleNotch size={14} className="animate-spin text-[var(--text-muted)]" />
                      <span className="text-xs text-[var(--text-muted)]">Loading agents...</span>
                    </div>
                  ) : agents.length === 0 ? (
                    <div className="p-3 bg-[var(--surface-2)] rounded-lg text-center">
                      <p className="text-xs text-[var(--text-muted)]">No agents found. Create one first.</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--border-default)] rounded-lg
                          text-left hover:border-[var(--border-strong)] transition-colors"
                        data-testid="agent-selector-button"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Robot size={16} className="text-[var(--brand-primary)] flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-white truncate">
                              {selectedAgent?.name || 'Select an agent...'}
                            </p>
                            {selectedAgent && (
                              <p className="text-[10px] text-[var(--text-muted)]">
                                {selectedAgent.capability_count} capabilities attached
                              </p>
                            )}
                          </div>
                        </div>
                        <CaretDown size={14} className={`text-[var(--text-muted)] transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown */}
                      <AnimatePresence>
                        {dropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            className="absolute top-full left-0 right-0 mt-1 bg-[var(--surface-1)] border border-[var(--border-default)] rounded-lg shadow-xl z-10 max-h-48 overflow-y-auto"
                            data-testid="agent-dropdown"
                          >
                            {agents.map(agent => (
                              <button
                                key={agent.id}
                                onClick={() => { setSelectedAgentId(agent.id); setDropdownOpen(false); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[var(--surface-3)] transition-colors
                                  ${agent.id === selectedAgentId ? 'bg-[var(--brand-primary)]/8' : ''}`}
                                data-testid={`agent-option-${agent.id}`}
                              >
                                <Robot size={14} className={agent.id === selectedAgentId ? 'text-[var(--brand-primary)]' : 'text-[var(--text-muted)]'} />
                                <div className="min-w-0 flex-1">
                                  <p className={`text-xs truncate ${agent.id === selectedAgentId ? 'text-[var(--brand-primary)] font-medium' : 'text-white'}`}>
                                    {agent.name}
                                  </p>
                                  <p className="text-[10px] text-[var(--text-muted)]">
                                    {agent.capability_count} capabilities
                                  </p>
                                </div>
                                {agent.id === selectedAgentId && (
                                  <CheckCircle size={14} weight="fill" className="text-[var(--brand-primary)] flex-shrink-0" />
                                )}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* Submit */}
                <button
                  onClick={handleAttach}
                  disabled={submitting || !selectedAgentId}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                    bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white text-sm font-medium
                    disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="add-to-agent-submit"
                >
                  {submitting ? (
                    <>
                      <CircleNotch size={14} className="animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus size={14} weight="bold" />
                      Add to {selectedAgent?.name || 'Agent'}
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          {/* Footer */}
          {!result && (
            <div className="px-5 py-3 border-t border-[var(--border-default)] bg-[var(--surface-2)]/30">
              <p className="text-[10px] text-[var(--text-muted)] text-center">
                Attached capabilities are available in Visual Flow and agent execution.
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddToAgentModal;
